import { supabase } from '../lib/supabase'

// Create organization
export const createOrganization = async (name, userId) => {
  const { data, error } = await supabase
    .from('organizations')
    .insert([{ name, owner_id: userId }])
    .select()
    .single()

  if (error) throw error

  // Add owner as member
  await supabase
    .from('organization_members')
    .insert([{
      organization_id: data.id,
      user_id: userId,
      role: 'owner'
    }])

  return data
}

// Get user organizations
export const getUserOrganizations = async (userId) => {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      id,
      role,
      organization:organizations(*)
    `)
    .eq('user_id', userId)

  if (error) return { data: null, error }
  
  // Transform to return organizations with role info
  const organizations = data?.map(item => ({
    ...item.organization,
    memberRole: item.role,
    memberId: item.id
  })) || []
  
  return { data: organizations, error: null }
}

// Get organization members
export const getOrganizationMembers = async (organizationId) => {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      id,
      role,
      created_at,
      user:users(id, email, full_name, avatar_url)
    `)
    .eq('organization_id', organizationId)

  if (error) return { data: null, error }
  return { data, error: null }
}

// Add member to organization (via invitation system)
export const addOrganizationMember = async (organizationId, email, role = 'member') => {
  // Since we can't query auth.users directly with anon key,
  // we create an invitation that the user can accept later
  
  // Validate role
  const validRoles = ['viewer', 'member', 'admin', 'owner']
  if (!validRoles.includes(role)) {
    return { data: null, error: new Error(`Invalid role: ${role}. Must be one of: ${validRoles.join(', ')}`) }
  }
  
  const { data: currentUser } = await supabase.auth.getUser()
  
  if (!currentUser?.user) {
    return { data: null, error: new Error('Not authenticated') }
  }

  const cleanEmail = email.toLowerCase().trim()

  // Check if there's an existing invitation
  const { data: existingInvitation } = await supabase
    .from('invitations')
    .select('id, status')
    .eq('organization_id', organizationId)
    .eq('email', cleanEmail)
    .single()

  // If there's an existing pending invitation, update it
  if (existingInvitation) {
    if (existingInvitation.status === 'pending') {
      // Update existing invitation
      const { data, error } = await supabase
        .from('invitations')
        .update({
          role: role,
          invited_by: currentUser.user.id,
          created_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
        })
        .eq('id', existingInvitation.id)
        .select()
        .single()

      if (error) return { data: null, error }
      return { data, error: null }
    } else {
      // Delete old invitation and create new one
      await supabase
        .from('invitations')
        .delete()
        .eq('id', existingInvitation.id)
    }
  }

  // Create new invitation
  const { data, error } = await supabase
    .from('invitations')
    .insert([{
      organization_id: organizationId,
      email: cleanEmail,
      role: role,
      invited_by: currentUser.user.id,
      status: 'pending'
    }])
    .select()
    .single()

  if (error) {
    return { data: null, error }
  }
  
  return { data, error: null }
}

// Remove member from organization
export const removeOrganizationMember = async (memberId) => {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId)

  if (error) return { data: null, error }
  return { data: true, error: null }
}

// Update member role
export const updateMemberRole = async (memberId, role) => {
  // Validate role
  const validRoles = ['viewer', 'member', 'admin', 'owner']
  if (!validRoles.includes(role)) {
    return { data: null, error: new Error(`Invalid role: ${role}. Must be one of: ${validRoles.join(', ')}`) }
  }
  
  const { data, error } = await supabase
    .from('organization_members')
    .update({ role })
    .eq('id', memberId)
    .select()
    .single()

  if (error) return { data: null, error }
  return { data, error: null }
}

// Update organization
export const updateOrganization = async (organizationId, updates) => {
  const { data, error } = await supabase
    .from('organizations')
    .update(updates)
    .eq('id', organizationId)
    .select()
    .single()

  if (error) return { data: null, error }
  return { data, error: null }
}
// Get invitations for an organization
export const getOrganizationInvitations = async (organizationId) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  if (error) return { data: null, error }
  return { data, error: null }
}

// Get user's pending invitations
export const getUserInvitations = async (email) => {
  const { data, error } = await supabase
    .from('invitations')
    .select(`
      *,
      organizations (
        id,
        name,
        slug
      )
    `)
    .eq('email', email.toLowerCase().trim())
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })

  if (error) return { data: null, error }
  return { data, error: null }
}

// Accept an invitation
export const acceptInvitation = async (invitationId) => {
  const { data, error } = await supabase.rpc('accept_invitation', {
    invitation_id: invitationId
  })

  if (error) return { data: null, error }
  return { data, error: null }
}

// Decline an invitation
export const declineInvitation = async (invitationId) => {
  const { data, error } = await supabase.rpc('decline_invitation', {
    invitation_id: invitationId
  })

  if (error) return { data: null, error }
  return { data, error: null }
}

// Cancel/delete an invitation (for admins/owners)
export const cancelInvitation = async (invitationId) => {
  const { data, error } = await supabase
    .from('invitations')
    .delete()
    .eq('id', invitationId)
    .select()
    .single()

  if (error) return { data: null, error }
  return { data, error: null }
}